import { useState, useEffect, useRef } from 'react'
import api from '../services/api'

const TIPOS = [
  { value: 'NFE', label: 'NF-e' },
  { value: 'CTEOS', label: 'CT-e OS' },
  { value: 'NFSE', label: 'NFS-e (Municipal e Nacional)' },
]

const SITUACOES = [
  { value: '6', label: 'Autorizada' },
  { value: '7', label: 'Cancelada' },
]

interface Nota {
  ntv_empcod: number; cliente: string; razao_social?: string; ntv_serie: string; ntv_numero: number; cli_cod: string; modelo?: string | null
  ntv_datemi: string; ntv_sitnfe: number; cfop: string | null; valor_nota: string
  inss_destaque: string; inss_retencao: string
  irrf_retencao: string
  issqn_destaque: string; issqn_retencao: string
  icms_destaque: string
  pcc_destaque: string; pcc_retencao: string
  valor_liquido: string; fatura_agrup: number
  cidade_origem?: string | null; cidade_destino?: string | null; cidade_prestacao?: string | null
}
interface Empresa { empcod: number; empnom: string; empfan: string; empcgc: string }

const SITUACAO_LABEL: Record<number, string> = { 6: 'Autorizada', 7: 'Cancelada' }

function hoje() { return new Date().toISOString().slice(0, 10) }
function primeiroDiaMes() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` }

function fmtMoeda(v: string | number) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtData(v: string) {
  return v ? String(v).slice(0, 10).split('-').reverse().join('/') : ''
}

// ── Combo com checkboxes (multi-seleção) ──────────────────────────────────────
function ComboCheck({ label, opcoes, selecionados, onChange, placeholder, width, dropdownWidth }: {
  label: string
  opcoes: { value: string; label: string }[]
  selecionados: string[]
  onChange: (v: string[]) => void
  placeholder: string
  width?: number
  dropdownWidth?: number
}) {
  const [aberto, setAberto] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function toggle(v: string) {
    onChange(selecionados.includes(v) ? selecionados.filter(x => x !== v) : [...selecionados, v])
  }

  const resumo = selecionados.length === 0
    ? placeholder
    : selecionados.length === opcoes.length
      ? 'Todas'
      : selecionados.length === 1
        ? opcoes.find(o => o.value === selecionados[0])?.label ?? '1 selecionada'
        : `${selecionados.length} selecionadas`

  return (
    <div className="field" style={{ position: 'relative', width: width ?? 200, flex: '0 0 auto' }} ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        onClick={() => setAberto(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: '#fff', border: '1px solid #d1d5db',
          borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: selecionados.length ? '#111827' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resumo}</span>
        <span style={{ color: '#9ca3af', marginLeft: 6 }}>{aberto ? '▴' : '▾'}</span>
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 2,
          width: dropdownWidth ?? '100%', minWidth: '100%',
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          maxHeight: 320, overflowY: 'auto', boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
            <button type="button" onClick={() => onChange(opcoes.map(o => o.value))}
              style={{ fontSize: '0.72rem', color: '#1a6ea8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize: '0.72rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
              Limpar
            </button>
          </div>
          {opcoes.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 10px', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={selecionados.includes(o.value)} onChange={() => toggle(o.value)}
                style={{ width: 15, height: 15, flexShrink: 0, margin: '2px 0 0' }} />
              <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default function RelatorioFaturamento() {
  const [dataIni, setDataIni] = useState(primeiroDiaMes())
  const [dataFim, setDataFim] = useState(hoje())
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])
  const [situacoesSel, setSituacoesSel] = useState<string[]>(['6'])
  const [cfopsSel, setCfopsSel] = useState<string[]>([])
  const [cfopOpcoes, setCfopOpcoes] = useState<{ cfop: string; cfop_des: string }[]>([])
  const [listarCidade, setListarCidade] = useState(false)
  const [apurarInss, setApurarInss] = useState(false)

  const [buscaCliente, setBuscaCliente] = useState('')
  const [clienteSel, setClienteSel] = useState<{ cli_cod: string; nome: string } | null>(null)
  const [sugestoesCliente, setSugestoesCliente] = useState<{ cli_cod: string; cli_nom: string; cli_fan: string }[]>([])

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<Nota[] | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
    api.get('/relatorios/faturamento/cfops').then(r => setCfopOpcoes(r.data)).catch(() => {})
  }, [])

  async function buscarClientes(termo: string) {
    setBuscaCliente(termo)
    setClienteSel(null)
    if (termo.trim().length < 2) { setSugestoesCliente([]); return }
    const r = await api.get('/erp/clientes/lista', { params: { busca: termo, por_pagina: 10 } })
    setSugestoesCliente(r.data)
  }
  function selecionarCliente(c: { cli_cod: string; cli_nom: string; cli_fan: string }) {
    setClienteSel({ cli_cod: c.cli_cod, nome: c.cli_fan || c.cli_nom })
    setBuscaCliente(c.cli_fan || c.cli_nom)
    setSugestoesCliente([])
  }

  function filtrosParams() {
    return {
      dataIni, dataFim,
      empresas: empresasSel.join(','),
      cliCod: clienteSel?.cli_cod,
      tipos: tiposSel.join(','),
      situacoes: situacoesSel.join(','),
      cfops: cfopsSel.join(','),
      listarCidade: listarCidade ? 'S' : 'N',
      apurarInss: apurarInss ? 'S' : 'N',
    }
  }

  async function gerar() {
    setCarregando(true)
    setErro('')
    setNotas(null)
    try {
      const r = await api.get('/relatorios/faturamento', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/faturamento/pdf', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (e: any) {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true)
    setErro('')
    try {
      const r = await api.get('/relatorios/faturamento/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'relatorio-faturamento.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totais = (notas ?? []).reduce((acc, n) => {
    acc.valorNota += Number(n.valor_nota || 0)
    acc.inssDest += Number(n.inss_destaque || 0)
    acc.inssRet += Number(n.inss_retencao || 0)
    acc.irrfRet += Number(n.irrf_retencao || 0)
    acc.issqnDest += Number(n.issqn_destaque || 0)
    acc.issqnRet += Number(n.issqn_retencao || 0)
    acc.icmsDest += Number(n.icms_destaque || 0)
    acc.pccDest += Number(n.pcc_destaque || 0)
    acc.pccRet += Number(n.pcc_retencao || 0)
    acc.liquido += Number(n.valor_liquido || 0)
    return acc
  }, { valorNota: 0, inssDest: 0, inssRet: 0, irrfRet: 0, issqnDest: 0, issqnRet: 0, icmsDest: 0, pccDest: 0, pccRet: 0, liquido: 0 })

  const totaisCfop = (() => {
    const mapa = new Map<string, number>()
    for (const n of notas ?? []) {
      const cfopBruto = (n.cfop ?? '').trim()
      // Nota sem CFOP (ex.: NFS-e Nacional) entra agrupada em '0' — senao o total dessa
      // tabela nunca fecha com o TOTAL geral do relatorio.
      const cfop = (!cfopBruto || cfopBruto === '0') ? '0' : cfopBruto
      mapa.set(cfop, (mapa.get(cfop) ?? 0) + Number(n.valor_nota || 0))
    }
    return Array.from(mapa.entries())
      .map(([cfop, valor]) => ({ cfop, valor }))
      .sort((a, b) => (a.cfop === '0' ? 1 : 0) - (b.cfop === '0' ? 1 : 0) || a.cfop.localeCompare(b.cfop))
  })()
  const totalCfopGeral = totaisCfop.reduce((s, c) => s + c.valor, 0)

  const apuracaoInss = (() => {
    if (!apurarInss) return []
    const relevantes = (notas ?? []).filter(n => Number(n.inss_retencao || 0) > 0)
    const mapa = new Map<string, { cli_cod: string; razao_social: string; vl_norm: number; inss_norm: number; vl_anulado: number; inss_anulado: number; vl_compensar: number }>()
    for (const n of relevantes) {
      const atual = mapa.get(n.cli_cod) ?? {
        cli_cod: n.cli_cod, razao_social: n.razao_social || n.cliente,
        vl_norm: 0, inss_norm: 0, vl_anulado: 0, inss_anulado: 0, vl_compensar: 0,
      }
      const valor = Number(n.valor_nota || 0)
      const inss = Number(n.inss_retencao || 0)
      if (n.ntv_sitnfe === 7) { atual.vl_anulado += valor; atual.inss_anulado += inss }
      else { atual.vl_norm += valor; atual.inss_norm += inss }
      mapa.set(n.cli_cod, atual)
    }
    const lista = Array.from(mapa.values())
    for (const c of lista) c.vl_compensar = c.inss_norm - c.inss_anulado
    return lista.sort((a, b) => a.razao_social.localeCompare(b.razao_social))
  })()
  const totalApuracaoInss = apuracaoInss.reduce((acc, a) => {
    acc.vl_norm += a.vl_norm; acc.inss_norm += a.inss_norm
    acc.vl_anulado += a.vl_anulado; acc.inss_anulado += a.inss_anulado; acc.vl_compensar += a.vl_compensar
    return acc
  }, { vl_norm: 0, inss_norm: 0, vl_anulado: 0, inss_anulado: 0, vl_compensar: 0 })

  const th: React.CSSProperties = { padding: '0.6rem 0.7rem', textAlign: 'right', color: '#fff', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' }
  const td: React.CSSProperties = { padding: '0.55rem 0.7rem', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap' }
  const tdLeft: React.CSSProperties = { ...td, textAlign: 'left' }
  const tdIdent: React.CSSProperties = { ...tdLeft, color: '#64748b' }

  // Divisor neutro entre grupos de imposto — só pra separar visualmente os blocos,
  // sem cores diferentes por grupo (ficava carregado).
  const DIVISOR = '1px solid #cbd5e1'
  const thGrupo: React.CSSProperties = { ...th, textAlign: 'center', borderLeft: DIVISOR }
  const thSub = (borda?: boolean): React.CSSProperties => ({ ...th, ...(borda ? { borderLeft: DIVISOR } : {}) })
  const tdGrupo: React.CSSProperties = { ...td, borderLeft: DIVISOR }

  function ValorCell({ v, style }: { v: string | number; style?: React.CSSProperties }) {
    const num = Number(v || 0)
    return <span style={{ color: num === 0 ? '#cbd5e1' : '#374151', ...style }}>{fmtMoeda(v)}</span>
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.1rem', color: '#1a6ea8' }}>
          Relatório do Faturamento
        </h2>

        {/* ── Período e origem ─────────────────────────────────────────── */}
        <div style={{ background: '#f7faff', borderLeft: '4px solid #1a6ea8', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão de</label>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} />
            </div>
            <div className="field" style={{ width: 130, flex: '0 0 auto' }}>
              <label>Emissão até</label>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
            </div>
            <ComboCheck
              label="Empresas"
              placeholder="Todas"
              width={200}
              opcoes={empresas.map(e => ({ value: String(e.empcod), label: `${e.empcod} — ${e.empfan || e.empnom}` }))}
              selecionados={empresasSel}
              onChange={setEmpresasSel}
            />
            <ComboCheck
              label="Tipo de NF"
              placeholder="Todos"
              width={210}
              opcoes={TIPOS}
              selecionados={tiposSel}
              onChange={setTiposSel}
            />
            <ComboCheck
              label="Situação da NF"
              placeholder="Autorizada + Cancelada"
              width={190}
              opcoes={SITUACOES}
              selecionados={situacoesSel}
              onChange={setSituacoesSel}
            />
            <ComboCheck
              label="CFOP"
              placeholder="Todos"
              width={280}
              dropdownWidth={480}
              opcoes={cfopOpcoes.map(c => ({
                value: c.cfop,
                label: c.cfop === '0' ? c.cfop_des : `${Number(c.cfop).toLocaleString('pt-BR')} — ${c.cfop_des}`,
              }))}
              selecionados={cfopsSel}
              onChange={setCfopsSel}
            />
          </div>
        </div>

        {/* ── Cliente ──────────────────────────────────────────────────── */}
        <div style={{ background: '#fff8f2', borderLeft: '4px solid #f07820', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-start' }}>
            <div className="field" style={{ position: 'relative', width: 260, flex: '0 0 auto' }}>
              <label>Cliente</label>
              <input
                type="text"
                placeholder="Buscar por nome... (vazio = todos)"
                value={buscaCliente}
                onChange={e => buscarClientes(e.target.value)}
              />
              {sugestoesCliente.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                  maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {sugestoesCliente.map(c => (
                    <button key={c.cli_cod} type="button" onClick={() => selecionarCliente(c)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.82rem' }}>
                      {c.cli_fan || c.cli_nom}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Opções ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: '1.2rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}>
            <input type="checkbox" checked={listarCidade} onChange={e => setListarCidade(e.target.checked)} />
            Listar cidade (origem/destino do CT-e, prestação da NFS-e)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}>
            <input type="checkbox" checked={apurarInss} onChange={e => setApurarInss(e.target.checked)} />
            Apurar INSS
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>
            {carregando ? 'Gerando...' : 'Gerar relatório'}
          </button>
          {notas && (
            <button
              type="button"
              onClick={gerarPdf}
              disabled={gerandoPdf}
              style={{
                padding: '0.5rem 1rem', background: '#dc2626', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoPdf ? 'not-allowed' : 'pointer',
                opacity: gerandoPdf ? 0.7 : 1,
              }}
            >
              {gerandoPdf ? 'Gerando PDF...' : 'PDF'}
            </button>
          )}
          {notas && (
            <button
              type="button"
              onClick={gerarExcel}
              disabled={gerandoExcel}
              style={{
                padding: '0.5rem 1rem', background: '#15803d', color: '#fff', border: 'none',
                borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: gerandoExcel ? 'not-allowed' : 'pointer',
                opacity: gerandoExcel ? 0.7 : 1,
              }}
            >
              {gerandoExcel ? 'Gerando Excel...' : 'Excel'}
            </button>
          )}
        </div>
        {erro && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{erro}</p>}
      </div>

      {notas && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notas</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a6ea8' }}>{notas.length}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Notas</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>R$ {fmtMoeda(totais.valorNota)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Líquido</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#15803d' }}>R$ {fmtMoeda(totais.liquido)}</div>
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#1a6ea8' }}>
                  <th rowSpan={2} style={thLeft}>Emp</th>
                  <th rowSpan={2} style={thLeft}>Cliente</th>
                  <th rowSpan={2} style={thLeft}>Mod.</th>
                  <th rowSpan={2} style={thLeft}>Nota</th>
                  <th rowSpan={2} style={thLeft}>Sér</th>
                  <th rowSpan={2} style={thLeft}>Emissão</th>
                  <th rowSpan={2} style={th}>Vlr. da Nota</th>
                  <th rowSpan={2} style={th}>INSS Ret.</th>
                  <th rowSpan={2} style={th}>IRRF Ret.</th>
                  <th colSpan={2} style={thGrupo}>ISSQN</th>
                  <th colSpan={1} style={thGrupo}>ICMS</th>
                  <th colSpan={2} style={thGrupo}>PIS/COF/CSLL</th>
                  <th rowSpan={2} style={th}>Vlr. Líquido</th>
                  <th rowSpan={2} style={th}>Fat. Agrup.</th>
                  {listarCidade && <th rowSpan={2} style={thLeft}>Cid. Origem</th>}
                  {listarCidade && <th rowSpan={2} style={thLeft}>Cid. Destino</th>}
                  {listarCidade && <th rowSpan={2} style={thLeft}>Cid. Prestação</th>}
                </tr>
                <tr style={{ background: '#1a6ea8' }}>
                  <th style={thSub(true)}>Dest.</th>
                  <th style={thSub()}>Ret.</th>
                  <th style={thSub(true)}>Dest.</th>
                  <th style={thSub(true)}>Dest.</th>
                  <th style={thSub()}>Ret.</th>
                </tr>
              </thead>
              <tbody>
                {notas.map((n, i) => (
                  <tr key={`${n.ntv_empcod}-${n.ntv_serie}-${n.ntv_numero}-${n.cli_cod}`}
                    style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #eef0f4' }}>
                    <td style={tdLeft}>{n.ntv_empcod}</td>
                    <td style={{ ...tdLeft, color: '#111827', fontWeight: 500, whiteSpace: 'normal' }}>{n.cliente}</td>
                    <td style={tdIdent}>{n.modelo || '-'}</td>
                    <td style={tdIdent}>{n.ntv_numero}</td>
                    <td style={tdIdent}>{n.ntv_serie}</td>
                    <td style={tdIdent}>{fmtData(n.ntv_datemi)}</td>
                    <td style={td}>{fmtMoeda(n.valor_nota)}</td>
                    <td style={td}><ValorCell v={n.inss_retencao} style={{ fontWeight: 700 }} /></td>
                    <td style={td}><ValorCell v={n.irrf_retencao} style={{ fontWeight: 700 }} /></td>
                    <td style={tdGrupo}><ValorCell v={n.issqn_destaque} /></td>
                    <td style={td}><ValorCell v={n.issqn_retencao} style={{ fontWeight: 700 }} /></td>
                    <td style={tdGrupo}><ValorCell v={n.icms_destaque} /></td>
                    <td style={tdGrupo}><ValorCell v={n.pcc_destaque} /></td>
                    <td style={td}><ValorCell v={n.pcc_retencao} style={{ fontWeight: 700 }} /></td>
                    <td style={{ ...td, color: '#15803d', fontWeight: 600 }}>{fmtMoeda(n.valor_liquido)}</td>
                    <td style={td}><ValorCell v={n.fatura_agrup ?? 0} /></td>
                    {listarCidade && <td style={tdLeft}>{n.cidade_origem || '-'}</td>}
                    {listarCidade && <td style={tdLeft}>{n.cidade_destino || '-'}</td>}
                    {listarCidade && <td style={tdLeft}>{n.cidade_prestacao || '-'}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                  <td colSpan={6} style={{ padding: '0.75rem 0.7rem', textAlign: 'right', color: '#1a6ea8' }}>TOTAL:</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totais.valorNota)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totais.inssRet)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totais.irrfRet)}</td>
                  <td style={{ ...tdGrupo, fontWeight: 700 }}>{fmtMoeda(totais.issqnDest)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totais.issqnRet)}</td>
                  <td style={{ ...tdGrupo, fontWeight: 700 }}>{fmtMoeda(totais.icmsDest)}</td>
                  <td style={{ ...tdGrupo, fontWeight: 700 }}>{fmtMoeda(totais.pccDest)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totais.pccRet)}</td>
                  <td style={{ ...td, color: '#15803d', fontWeight: 700 }}>{fmtMoeda(totais.liquido)}</td>
                  <td style={td}></td>
                  {listarCidade && <td></td>}
                  {listarCidade && <td></td>}
                  {listarCidade && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {totaisCfop.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.75rem' }}>
                Totais das CFOP's no Período (Valor Contábil)
              </h3>
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb', maxWidth: 420 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: '#1a6ea8' }}>
                      <th style={thLeft}>CFOP</th>
                      <th style={th}>Valor Contábil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totaisCfop.map((c, i) => (
                      <tr key={c.cfop} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #eef0f4' }}>
                        <td style={tdLeft}>{c.cfop === '0' ? 'Nacional' : Number(c.cfop).toLocaleString('pt-BR')}</td>
                        <td style={td}>{fmtMoeda(c.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                      <td style={{ padding: '0.65rem 0.8rem', textAlign: 'left', color: '#1a6ea8' }}>Total do Período</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalCfopGeral)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {apurarInss && apuracaoInss.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.75rem' }}>
                Detalhamento de INSS Retido no Período por Cliente
              </h3>
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: '#1a6ea8' }}>
                      <th style={thLeft}>CNPJ Cliente</th>
                      <th style={thLeft}>Razão Social</th>
                      <th style={th}>VL NFs Norm/Comp</th>
                      <th style={th}>INSS RET Norm/Comp</th>
                      <th style={th}>VL Anulado</th>
                      <th style={th}>INSS Anulado</th>
                      <th style={th}>Vl a Compensar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apuracaoInss.map((a, i) => (
                      <tr key={a.cli_cod} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #eef0f4' }}>
                        <td style={tdLeft}>{a.cli_cod}</td>
                        <td style={{ ...tdLeft, color: '#111827', fontWeight: 500 }}>{a.razao_social}</td>
                        <td style={td}>{fmtMoeda(a.vl_norm)}</td>
                        <td style={td}>{fmtMoeda(a.inss_norm)}</td>
                        <td style={td}>{fmtMoeda(a.vl_anulado)}</td>
                        <td style={td}>{fmtMoeda(a.inss_anulado)}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{fmtMoeda(a.vl_compensar)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #1a6ea8', background: '#f7faff', fontWeight: 700 }}>
                      <td colSpan={2} style={{ padding: '0.65rem 0.8rem', textAlign: 'left', color: '#1a6ea8' }}>Total do Período</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalApuracaoInss.vl_norm)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalApuracaoInss.inss_norm)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalApuracaoInss.vl_anulado)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalApuracaoInss.inss_anulado)}</td>
                      <td style={{ ...td, fontWeight: 700 }}>{fmtMoeda(totalApuracaoInss.vl_compensar)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
